/**
 * 邮箱验证页面组件
 *
 * 功能:
 * - 显示验证提示信息
 * - 6位验证码输入框
 * - 重发验证码按钮（带倒计时）
 * - 支持验证链接验证
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VerificationCodeInput } from '../components/VerificationCodeInput';
import { SliderCaptcha } from '../components/SliderCaptcha';
import { authApi, AuthApiError, toUser } from '../services/authApi';
import { useAuthStore } from '../store/authStore';

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') || '';
  const tokenFromQuery = searchParams.get('token') || '';

  const [email, setEmail] = useState(emailFromQuery);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isTokenVerification, setIsTokenVerification] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);

  const setUser = useAuthStore((state) => state.setUser);
  const setToken = useAuthStore((state) => state.setToken);

  const handleAuthSuccess = useCallback(
    (response: { user?: any; token?: string }) => {
      if (response.user) {
        const user = toUser(response.user);
        setUser(user);
      }
      if (response.token) {
        setToken(response.token);
      }
      useAuthStore.setState({
        isAuthenticated: true,
        initialized: true,
        loading: false,
      });
      navigate('/author-center', { replace: true });
    },
    [navigate, setUser, setToken]
  );

  useEffect(() => {
    if (tokenFromQuery && !isTokenVerification) {
      setIsTokenVerification(true);
      setLoading(true);
      setError('');
      authApi
        .verifyEmailToken(tokenFromQuery)
        .then((response) => {
          setSuccess(response.message);
          handleAuthSuccess(response);
        })
        .catch((err) => {
          if (err instanceof AuthApiError) {
            setError(err.message);
          } else {
            setError('验证链接无效或已过期');
          }
        })
        .finally(() => setLoading(false));
    }
  }, [tokenFromQuery, isTokenVerification, handleAuthSuccess]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (code.length !== 6) {
        setError('请输入6位验证码');
        return;
      }
      if (!email) {
        setError('请输入邮箱地址');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await authApi.verifyEmailCode(email, code);
        setSuccess(response.message);
        handleAuthSuccess(response);
      } catch (err) {
        if (err instanceof AuthApiError) {
          setError(err.message);
        } else {
          setError('验证失败，请稍后重试');
        }
      } finally {
        setLoading(false);
      }
    },
    [email, code, handleAuthSuccess]
  );

  const handleResend = useCallback(async () => {
    if (!email) {
      setError('请输入邮箱地址');
      return;
    }
    if (countdown > 0) return;

    setShowCaptcha(true);
  }, [email, countdown]);

  const handleCaptchaSuccess = useCallback(async (captchaToken: string) => {
    setShowCaptcha(false);
    setResendLoading(true);
    setError('');

    try {
      await authApi.resendVerification(email, captchaToken);
      setCountdown(60);
      setSuccess('验证邮件已重新发送');
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(err.message);
      } else {
        setError('发送失败，请稍后重试');
      }
    } finally {
      setResendLoading(false);
    }
  }, [email]);

  const handleBackToLogin = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  const uiText = useMemo(
    () => ({
      title: '验证您的邮箱',
      subtitle: `我们已向 ${email || '您的邮箱'} 发送了一封验证邮件`,
      buttonText: loading ? '验证中...' : '验证并登录',
      resendText:
        countdown > 0
          ? `重新发送 (${countdown}s)`
          : resendLoading
          ? '发送中...'
          : '重新发送验证码',
    }),
    [email, loading, countdown, resendLoading]
  );

  if (isTokenVerification) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{uiText.title}</h1>
          </div>
          <div className="bg-white rounded-lg shadow-md p-8">
            {loading && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500 mx-auto mb-4"></div>
                <p className="text-gray-600">正在验证...</p>
              </div>
            )}
            {error && !loading && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={handleBackToLogin}
                  className="mt-4 text-sm text-paper-600 hover:text-paper-700"
                >
                  返回登录
                </button>
              </div>
            )}
            {success && !loading && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{uiText.title}</h1>
          <p className="text-gray-600">{uiText.subtitle}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {!email && (
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                  邮箱地址
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="请输入注册时使用的邮箱"
                  disabled={loading || resendLoading}
                />
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-3">验证码</label>
              <VerificationCodeInput
                value={code}
                onChange={setCode}
                disabled={loading}
                autoFocus={!!email}
                className="justify-center"
              />
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full bg-ink-800 hover:bg-ink-700 disabled:bg-ink-400 text-white px-6 py-3 rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:hover:translate-y-0"
            >
              {uiText.buttonText}
            </button>

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 mb-2">没收到邮件？</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={countdown > 0 || resendLoading || !email}
                className="text-sm text-paper-600 hover:text-paper-700 disabled:text-gray-400 font-medium"
              >
                {uiText.resendText}
              </button>
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← 返回登录
              </button>
            </div>
          </form>
        </div>
      </div>

      {showCaptcha && (
        <SliderCaptcha
          onSuccess={handleCaptchaSuccess}
          onClose={() => setShowCaptcha(false)}
        />
      )}
    </div>
  );
};

export default VerifyEmailPage;